import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "../../assets/img/closeIcon.svg";
import { haveValue, isObject } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import UserIcon from "../../assets/img/user-icon.svg";
import LeftArrow from "../../assets/img/arrow-left.svg";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const FinancialInfoModal = ({
  user,
  finData,
  isOpen,
  onClose,
  handleFinancialIncomeChange,
  storedFinancialData,
  selectedSources,
  setSelectedSources,
}) => {
  const { _t } = useTranslator();
  const incomeData = finData?.data?.category_breakdown?.income || {};
  const params = new URLSearchParams(window.location.search);
  const guarantor = params.get("guarantor");

  useEffect(() => {
    if (!isOpen) return;

    let data = haveValue(guarantor)
      ? storedFinancialData?.guarantor_sources?.[guarantor]
      : storedFinancialData?.tenant_source;

    if (Array.isArray(data) && data?.length > 0) {
      setSelectedSources(Array.isArray(data) ? data : []);
    } else {
      setSelectedSources(["Salary", "Income", "Rental Income"]);
    }
  }, [guarantor, isOpen, storedFinancialData]);

  const [showHousingSection, setShowHousingSection] = useState(false);
  const [housingObj, setHousingObj] = useState({
    selected: [],
    present: {},
    categoires: [],
  });
  const [eligibleSources, seteligibleSources] = useState({
    total: 0,
    count: 0,
    percentage: 0,
  });
  const [inEligibleSources, setInEligibleSources] = useState({
    total: 0,
    count: 0,
    percentage: 0,
  });
  const percentageVals = useMemo(() => {
    const tempData = {
      eligible: 0,
      inEligible: 0,
      Housing: 0,
      CE: 0,
      DE: 0,
      BEE: 0,
      BQL: 0,
    };

    if (haveValue(finData?.data?.category_breakdown?.expense)) {
      const expense = finData.data.category_breakdown.expense;
      for (const key in expense) {
        tempData[key] = expense[key].percentage / 2;
      }
    }

    tempData.inEligible = parseFloat(
      (inEligibleSources?.percentage ?? 0) / 2
    ).toFixed(2);
    tempData.eligible = parseFloat(
      (eligibleSources?.percentage ?? 0) / 2
    ).toFixed(2);

    return tempData;
  }, [finData, eligibleSources, inEligibleSources]);

  useEffect(() => {
    if (showHousingSection) {
      // Wait for DOM to render the section
      setTimeout(() => {
        const modal = document.querySelector(".financialInfoModal");
        if (modal) modal.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [showHousingSection]);

  const handleCheckboxChange = (key) => {
    const updatedSelectedSources = selectedSources.includes(key)
      ? selectedSources.filter((item) => item !== key)
      : [...selectedSources, key];
    setSelectedSources(updatedSelectedSources);
    handleFinancialIncomeChange(
      updatedSelectedSources,
      haveValue(guarantor),
      haveValue(guarantor) ? guarantor : null
    );
  };

  useEffect(() => {
    if (!incomeData) return;

    let eligibleTotal = 0;
    let eligibleCount = 0;
    let eligiblePercentage = 0;
    let ineligibleTotal = 0;
    let ineligibleCount = 0;
    let ineligiblePercentage = 0;

    Object.entries(incomeData).forEach(([key, value]) => {
      if (key != "monthSet") {
        if (selectedSources.includes(key) && key != "Ineligible") {
          eligibleTotal += value.total;
          eligibleCount += value.count;
          eligiblePercentage += value.percentage;
        } else {
          ineligibleTotal += value.total;
          ineligibleCount += value.count;
          ineligiblePercentage += value.percentage;
        }
      }
    });

    seteligibleSources({
      total: eligibleTotal,
      count: eligibleCount,
      percentage: eligiblePercentage,
    });

    setInEligibleSources({
      total: ineligibleTotal,
      count: ineligibleCount,
      percentage: ineligiblePercentage,
    });
  }, [selectedSources, finData]);

  useEffect(() => {
    if (
      haveValue(finData) &&
      isObject(finData?.data?.housingBifercation?.uniqueMonths) &&
      Object.keys(finData?.data?.housingBifercation?.uniqueMonths)?.length > 0
    ) {
      let selected = [];
      let present = {};

      Object.keys(finData?.data?.housingBifercation).map((keyVal) => {
        if (keyVal != "uniqueMonths") {
          if (finData?.data?.housingBifercation?.[keyVal]?.length > 0) {
            selected.push(keyVal);
            present[keyVal] = finData?.data?.housingBifercation?.[keyVal];
          }
        }
      });

      setHousingObj({ selected, present, categoires: selected });
    }
  }, [finData]);

  if (!isOpen) return;

  const ChartComponent = React.memo(({ percentageVals }) => {
    const chartData = useMemo(
      () => ({
        labels: [
          _t("eligible"),
          _t("inEligible"),
          _t("Housing"),
          _t("CE"),
          _t("DE"),
          _t("BEE"),
          _t("BQL"),
        ],
        datasets: [
          {
            data: Object.values(percentageVals),
            backgroundColor: [
              "#576E6D",
              "#465B5A",
              "#A3EB64",
              "#90DC4E",
              "#75C431",
              "#70B533",
              "#679E36",
            ],
            borderWidth: 0,
          },
        ],
      }),
      [percentageVals]
    );

    const chartOptions = useMemo(
      () => ({
        cutout: "50%",
        animation: { duration: 0 },
        // rotation: -90, // optional: starting angle
        // circumference: -360,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
      }),
      []
    );

    return (
      <div
        style={{ width: "10vw", height: "10vw" }}
        className="doughnutChartWrapper"
      >
        <Doughnut data={chartData} options={chartOptions} />
      </div>
    );
  });

  const housingSectionFunc = () => {
    if (
      haveValue(finData) &&
      isObject(finData?.data?.housingBifercation?.uniqueMonths) &&
      Object.keys(finData?.data?.housingBifercation?.uniqueMonths)?.length > 0
    ) {
      setShowHousingSection(true);
    }
  };

  const changeSelectedHousingObj = (e) => {
    const value = e.target.name;

    setHousingObj((prev) => {
      const isSelected = prev.selected.includes(value);
      const updatedSelected = isSelected
        ? prev.selected.filter((v) => v !== value) // remove
        : [...prev.selected, value]; // add

      return {
        ...prev,
        selected: updatedSelected,
      };
    });
  };

  return showHousingSection ? (
    <>
      <div className="wrapper-modal" onClick={onClose}>
        <div
          className={`doc-history-popup total-salary-modal wrapper-modal info-modal monthlyRentModal financialInfoModal ${
            isOpen ? "open" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="doc-history-header justify-content-between">
            <div className="d-flex align-items-center custom-gap-2">
              <img
                src={LeftArrow}
                alt=""
                className="doc-history-close-btn"
                onClick={(e) => setShowHousingSection(false)}
              />
              <h4 className="custom-modal-heading">
                {_t(
                  "monthlyRentAndUtilityBillHistory",
                  "Heading for modal when LL checks Score details and click on housing expenses."
                )}
              </h4>
            </div>
          </div>
          <div className="checkboxContainer">
            {housingObj?.categoires?.map((category) => {
              return (
                <div key={category} className="checkboxWrapper">
                  <label className="custom-gap-3 custom-checkbox">
                    <input
                      type="checkbox"
                      name={category}
                      checked={housingObj?.selected?.includes(category)}
                      onClick={changeSelectedHousingObj}
                    />
                    <span className="custom-checkmark"></span>
                    {_t(category)}
                  </label>
                </div>
              );
            })}
          </div>
          <div>
            <ul className="rentListContainer">
              <li className="d-flex rentListFirstContainer justify-content-between">
                <p>{_t("date")}</p>
                <p>{_t("bill")}</p>
                <p>{_t("amount")}</p>
              </li>
              {housingObj?.selected?.map((selectedKey) =>
                housingObj.present[selectedKey].map((val) => (
                  <li className="d-flex justify-content-between">
                    <p>{val["Booked Date"]}</p>
                    <p>{_t(val["ChildSubCategory"])}</p>
                    <p>€{Math.abs(val["Amount (Scaled Value)"])}</p>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </>
  ) : (
    <div className="wrapper-modal" onClick={onClose}>
      <div
        className={`doc-history-popup total-salary-modal wrapper-modal info-modal financialInfoModal ${
          isOpen ? "open" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="doc-history-header justify-content-between">
          <div className="d-flex align-items-center custom-gap-2">
            <img
              src={CloseIcon}
              alt=""
              className="doc-history-close-btn"
              onClick={onClose}
            />
            <h4 className="custom-modal-heading">
              {_t("financialInformation")}
            </h4>
          </div>
          <div className="d-flex align-items-center custom-gap-2">
            <div className="userIconWrapper">
              <img src={UserIcon} alt="User Icon" />
            </div>
            <p className="userName">
              {user?.fname ?? "N/A"} {user?.lname ?? ""}
            </p>
          </div>
        </div>
        <div className="sourceOfDataWrapper d-flex align-items-center custom-gap-2">
          <img src="/images/instruction-icon-grey.svg" />
          <p>
            <a
              href="/pdf/taxonomy.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t("clickHere")}
            </a>
            {_t("toCheckTheSourceOfData")}
          </p>
        </div>
        {finData?.status === "completed" ? (
          <div>
            <div className="">
              <div className="custom-gap-2 borderWrapper">
                <div className="borderWrapperItem borderWrapperItemFirst">
                  <p>{_t("totalTransactions")}</p>
                  <p className="count">
                    {finData?.data?.total_transactions ?? ""}
                  </p>
                </div>
                <div className="borderWrapperItem borderWrapperItemFirst">
                  <p>{_t("monthsWithData")}</p>
                  <p className="count">{finData?.data?.months_with_data}</p>
                </div>
                <div className="borderWrapperItem ">
                  <p className="word-break">
                    {_t("monthsWithExpenseTransactions")}
                  </p>
                  <p className="count">
                    {(isObject(finData?.data?.expenseMonths) &&
                      Object.keys(finData?.data?.expenseMonths ?? {}).length) ??
                      0}
                  </p>
                </div>
              </div>
              <div
                className={`custom-gap-2 borderWrapper ${
                  isObject(finData?.data?.housingBifercation?.uniqueMonths) &&
                  Object?.keys(
                    finData?.data?.housingBifercation?.uniqueMonths || {}
                  ).length > 0
                    ? "borderWrapperSecond"
                    : ""
                }`}
              >
                <div className="borderWrapperItem borderWrapperItemFirst">
                  <p className="word-break">
                    {_t("monthsWithIncomeTransactions")}
                  </p>
                  <p className="count">
                    {(isObject(finData?.data?.incomeMonths) &&
                      Object.keys(finData?.data?.incomeMonths ?? {}).length) ??
                      0}
                  </p>
                </div>
                <div
                  className={`borderWrapperItem ${
                    isObject(finData?.data?.housingBifercation?.uniqueMonths) &&
                    Object?.keys(
                      finData?.data?.housingBifercation?.uniqueMonths || {}
                    ).length > 0
                      ? "borderWrapperItemFirst"
                      : ""
                  }`}
                >
                  <p className="word-break">{_t("numberOfEligibleIncomes")}</p>
                  <p className="count">{eligibleSources?.count ?? 0}</p>
                </div>
                {isObject(finData?.data?.housingBifercation?.uniqueMonths) &&
                  Object?.keys(
                    finData?.data?.housingBifercation?.uniqueMonths || {}
                  ).length > 0 && (
                    <div
                      className="borderWrapperItem cursor-pointer"
                      onClick={housingSectionFunc}
                    >
                      <p className="word-break">
                        {_t(
                          "monthsWithRentAndUtilityBills",
                          "Used in score details model when LL clicks on a tenant score."
                        )}
                      </p>
                      <div className="d-flex justify-content-between">
                        <p className="count">
                          {(isObject(
                            finData?.data?.housingBifercation?.uniqueMonths
                          ) &&
                            Object.keys(
                              finData?.data?.housingBifercation?.uniqueMonths
                            )?.length) ??
                            0}
                        </p>
                        <img src="/images/arrow-right-green.svg" alt="" />
                      </div>
                    </div>
                  )}
              </div>
            </div>
            <div>
              <h4 className="heading">{_t("finSummary")}</h4>
              <div className="wrapperDetails d-flex custom-gap-3 align-items-center chartWrapper justify-content-between">
                <div className="detailsView">
                  <h6 style={{ "--after-color": "#679E36" }}>
                    {_t("expenses")}
                  </h6>
                  <p className="expenseAmount">
                    {parseFloat(finData?.data?.negativeSum).toFixed(2) ?? 0}{" "}
                    <p>
                      {" "}
                      {parseFloat(
                        (finData?.data?.negativeSum /
                          finData?.data?.totalAbsoluteSum) *
                          100
                      ).toFixed(2)}
                      %{" "}
                    </p>
                  </p>
                </div>

                <ChartComponent percentageVals={percentageVals} />
                <div className="detailsView incomeDetails">
                  <h6 style={{ "--after-color": "#576E6D" }}>{_t("income")}</h6>
                  <p className="expenseAmount">
                    {parseFloat(finData?.data?.positiveSum).toFixed(2) ?? 0}{" "}
                    <p>
                      {" "}
                      {parseFloat(
                        (finData?.data?.positiveSum /
                          finData?.data?.totalAbsoluteSum) *
                          100
                      ).toFixed(2)}
                      %{" "}
                    </p>
                  </p>
                </div>
              </div>
              <div className="custom-gap-2 expansesDetailCardWrapper">
                <div className="mainWrapper">
                  <div className="expansesDetailCard">
                    {haveValue(
                      finData?.data?.category_breakdown?.expense?.BQL
                    ) && (
                      <div className="detailsView">
                        <h6 style={{ "--after-color": "#679E36" }}>
                          {_t("basicQualityOfLiving")}
                        </h6>
                        <p className="expenseAmount">
                          {parseFloat(
                            finData?.data?.category_breakdown?.expense?.BQL
                              ?.total
                          ).toFixed(2)}
                          <p>
                            {parseFloat(
                              finData?.data?.category_breakdown?.expense?.BQL
                                ?.percentage
                            ).toFixed(2)}
                            %
                          </p>
                        </p>
                      </div>
                    )}
                    {haveValue(
                      finData?.data?.category_breakdown?.expense?.BEE
                    ) && (
                      <div className="detailsView">
                        <h6 style={{ "--after-color": "#70B533" }}>
                          {_t("basicEssentialExpenditure")}
                        </h6>
                        <p className="expenseAmount">
                          {parseFloat(
                            finData?.data?.category_breakdown?.expense?.BEE
                              ?.total
                          ).toFixed(2)}
                          <p>
                            {parseFloat(
                              finData?.data?.category_breakdown?.expense?.BEE
                                ?.percentage
                            ).toFixed(2)}
                            %
                          </p>
                        </p>
                      </div>
                    )}
                    {haveValue(
                      finData?.data?.category_breakdown?.expense?.DE
                    ) && (
                      <div className="detailsView">
                        <h6 style={{ "--after-color": "#75C431" }}>
                          {_t("disposableExpenditure")}
                        </h6>
                        <p className="expenseAmount">
                          {parseFloat(
                            finData?.data?.category_breakdown?.expense?.DE
                              ?.total
                          ).toFixed(2)}
                          <p>
                            {parseFloat(
                              finData?.data?.category_breakdown?.expense?.DE
                                ?.percentage
                            ).toFixed(2)}
                            %
                          </p>
                        </p>
                      </div>
                    )}
                    {haveValue(
                      finData?.data?.category_breakdown?.expense?.CE
                    ) && (
                      <div className="detailsView">
                        <h6 style={{ "--after-color": "#90DC4E" }}>
                          {_t("commitedExpenditure")}
                        </h6>
                        <p className="expenseAmount">
                          {parseFloat(
                            finData?.data?.category_breakdown?.expense?.CE
                              ?.total
                          ).toFixed(2)}
                          <p>
                            {parseFloat(
                              finData?.data?.category_breakdown?.expense?.CE
                                ?.percentage
                            ).toFixed(2)}
                            %
                          </p>
                        </p>
                      </div>
                    )}
                    {haveValue(
                      finData?.data?.category_breakdown?.expense?.Housing
                    ) && (
                      <div className="detailsView">
                        <h6 style={{ "--after-color": "#A3EB64" }}>
                          {_t("housing")}
                        </h6>
                        <p className="expenseAmount">
                          {parseFloat(
                            finData?.data?.category_breakdown?.expense?.Housing
                              ?.total
                          ).toFixed(2)}
                          <p>
                            {parseFloat(
                              finData?.data?.category_breakdown?.expense
                                ?.Housing?.percentage
                            ).toFixed(2)}
                            %
                          </p>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mainWrapper">
                  <div className="expansesDetailCard expansesDetailCardIncome">
                    <div className="detailsView">
                      <h6 style={{ "--after-color": "#465B5A" }}>
                        {_t("inEligibleIncome")}
                      </h6>
                      <p className="expenseAmount">
                        {parseFloat(inEligibleSources?.total).toFixed(2) ?? 0}
                        <p>
                          {parseFloat(inEligibleSources?.percentage).toFixed(
                            2
                          ) ?? 0}
                          %
                        </p>
                      </p>
                    </div>
                    <div className="detailsView">
                      <h6 style={{ "--after-color": "#576E6D" }}>
                        {_t("eligibleIncome")}
                      </h6>
                      <p className="expenseAmount">
                        {parseFloat(eligibleSources?.total).toFixed(2) ?? 0}
                        <p>
                          {parseFloat(eligibleSources?.percentage).toFixed(2) ??
                            0}
                          %
                        </p>
                      </p>
                    </div>

                    <div>
                      <span className="eligibleHeading">
                        {_t("eligibleIncomeSource")}
                      </span>
                      <div className="">
                        {isObject(incomeData) &&
                          Object.keys(incomeData).map((key) => {
                            const { total, count } = incomeData[key] || {};
                            if (key !== "Ineligible" && key != "monthSet")
                              return (
                                <div key={key} className="checkboxWrapper">
                                  <label className="custom-gap-3 custom-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={selectedSources.includes(key)}
                                      onChange={() => handleCheckboxChange(key)}
                                    />
                                    <span className="custom-checkmark"></span>
                                    {_t(
                                      key,
                                      "Text shown as income sources in financial information model"
                                    )}
                                  </label>
                                </div>
                              );
                          })}
                      </div>
                    </div>
                    <p className="incomeSourceText">
                      {_t(
                        "incomeSourceNotSelectedWillBeConsideredAsIneligibleIncome"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : finData?.status === "processing" ? (
          <div>
            {_t(
              "financialDataProcessingIsUnderProcess",
              "Text used in financial information modal"
            )}
          </div>
        ) : (
          <div>
            {_t(
              "financialDataNotFound",
              "Text used in financial information modal"
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialInfoModal;
